import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { findArticle } from "./reading";
import { RAW } from "./corpus";
import { ArticleBody, ProvenanceNote, SectionHeader, SourceBadge } from "./components";

export function PortalArticle() {
  const { brand, brandId, spaceId, user } = usePortal();
  const { id } = useParams({ strict: false }) as { id: string };
  const article = findArticle(brandId, id, spaceId);
  const [helpful, setHelpful] = useState<null | boolean>(null);

  if (!article) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "60px 28px", textAlign: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#1d241f" }}>We couldn't find that article</div>
        <p style={{ fontSize: 13.5, color: "#8a8e88", marginTop: 8 }}>It may have moved or been retired.</p>
        <Link to="/portal/home" style={{ color: brand.ink, fontSize: 13.5 }}>Back to home</Link>
      </div>
    );
  }

  const tenant = RAW.tenantAuthored.includes(article.id);
  const gated = article.plan === "Add-on";
  // Robust against malformed/other blocks: only pull `["h", text]` pairs.
  const headings = (article.body as unknown[])
    .filter((b): b is [string, string] => Array.isArray(b) && b[0] === "h" && typeof b[1] === "string")
    .map((b) => b[1]);
  const isVideo = article.kind === "video";

  return (
    <div style={{ maxWidth: 1120, margin: "0 auto", padding: "26px 28px 72px" }}>
      {/* breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#8a8e88", marginBottom: 16 }}>
        <Link to="/portal/home" style={{ color: "#8a8e88", textDecoration: "none" }}>Home</Link>
        <Icon name="chevron-right" size={13} color="#bcbfba" />
        <Link to="/portal/guides" search={{ cat: article.cat }} style={{ color: "#8a8e88", textDecoration: "none" }}>{article.cat}</Link>
      </div>

      {gated && (
        <div style={{ display: "flex", gap: 11, alignItems: "flex-start", background: "#fdf6ec", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(191,124,21,.3)", padding: "13px 15px", marginBottom: 18 }}>
          <Icon name="lightbulb" size={16} color="#a06508" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#7a4d05" }}>This covers an add-on {user.org} does not license</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#8a6a2f", marginTop: 3, textWrap: "pretty" }}>You can read it, but the screens it describes will not appear in your account. <Link to="/portal/new-ticket" style={{ color: "#7a4d05" }}>Ask about adding it</Link>.</div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 260px)", gap: 40, alignItems: "start" }}>
        <article style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <SourceBadge source={{ label: tenant ? user.org : brand.name, tenant }} brand={brand} />
            <span style={{ fontSize: 12, color: "#8a8e88" }}>{article.audience}</span>
          </div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 600, letterSpacing: "-1px", lineHeight: 1.2, color: "#1d241f", textWrap: "pretty" }}>{article.title}</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, marginBottom: 26, fontSize: 12.5, color: "#8a8e88" }}>
            <span>{isVideo ? `Watch · ${article.len}` : `${article.mins} min read`}</span>
            <span>·</span>
            <span>Updated {article.updated}</span>
            <span>·</span>
            <span style={{ fontFamily: "'Space Mono', monospace" }}>{article.views} views</span>
          </div>

          {(article.body as unknown[]).length > 0 ? (
            <ArticleBody blocks={article.body as unknown[][]} brand={brand} />
          ) : (
            <div style={{ border: "1px dashed #dcdad2", background: "#fafaf7", borderRadius: 3, padding: "26px 20px", textAlign: "center", color: "#8a8e88", fontSize: 13.5, textWrap: "pretty" }}>
              Full text not written for the prototype. In production this article's body renders here from the knowledge store.
            </div>
          )}

          <div style={{ marginTop: 34 }}>
            <ProvenanceNote tenant={tenant} orgName={user.org} brand={brand} />
          </div>

          {/* helpful */}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #f2f2f2", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {helpful === null ? (
              <>
                <span style={{ fontSize: 13.5, color: "#4a504a" }}>Was this helpful?</span>
                <button onClick={() => setHelpful(true)} style={{ border: "1px solid #dcdad2", background: "#fff", borderRadius: 3, padding: "7px 16px", fontSize: 13, fontWeight: 600, color: "#4a504a", cursor: "pointer" }}>Yes</button>
                <button onClick={() => setHelpful(false)} style={{ border: "1px solid #dcdad2", background: "#fff", borderRadius: 3, padding: "7px 16px", fontSize: 13, fontWeight: 600, color: "#4a504a", cursor: "pointer" }}>No</button>
              </>
            ) : (
              <span style={{ fontSize: 13.5, color: "#4a504a", display: "flex", alignItems: "center", gap: 7 }}>
                <Icon name="check-circle" size={16} color="#1f8a5b" />
                {helpful ? "Thanks — glad it helped." : "Thanks — we'll use this to improve the article."}
              </span>
            )}
            <span style={{ flex: 1 }} />
            <Link to="/portal/new-ticket" style={{ fontSize: 13, color: brand.ink, textDecoration: "none" }}>Open a ticket instead →</Link>
          </div>
        </article>

        {/* right rail */}
        <aside style={{ display: "flex", flexDirection: "column", gap: 22, position: "sticky", top: 0 }}>
          {headings.length > 0 && (
            <div>
              <div style={{ marginBottom: 10 }}><SectionHeader>On this page</SectionHeader></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, borderLeft: "2px solid #f0efe9", paddingLeft: 12 }}>
                {headings.map((h, i) => (
                  <span key={i} style={{ fontSize: 12.5, color: "#4a504a", lineHeight: 1.4 }}>{h}</span>
                ))}
              </div>
            </div>
          )}
          <div>
            <div style={{ marginBottom: 10 }}><SectionHeader>Applies to</SectionHeader></div>
            <div style={{ fontSize: 12.5, color: "#4a504a", lineHeight: 1.6 }}>
              {article.audience}s{tenant ? ` at ${user.org}` : ` · every ${brand.name} organization`}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
